export interface RecordingStudioExportTrack {
  blob: Blob
  volume: number
  offsetMs: number
  timelineStartMs?: number
  clipStartMs?: number
  clipEndMs?: number
}

interface DecodedTrack {
  buffer: AudioBuffer
  volume: number
  offsetMs: number
  timelineStartMs: number
  clipStartMs: number
  clipEndMs: number
}

export function sanitizeExportVolume(value: number): number {
  if (!Number.isFinite(value)) return 1
  return Math.min(1, Math.max(0, value))
}

export function exportedFrameCount(
  durationSeconds: number,
  sampleRate: number,
  offsetMs: number,
  timelineStartMs = 0,
): number {
  const sourceFrames = Math.max(0, Math.ceil(durationSeconds * sampleRate))
  const skipFrames = Math.max(0, Math.round((Math.max(0, offsetMs) / 1000) * sampleRate))
  const audibleFrames = Math.max(0, sourceFrames - skipFrames)
  const startFrames = Math.max(0, Math.round((Math.max(0, timelineStartMs) / 1000) * sampleRate))
  return startFrames + audibleFrames
}

function encodeWav(channels: Float32Array[], sampleRate: number): Blob {
  const channelCount = channels.length
  const frameCount = channels[0]?.length ?? 0
  const bytesPerSample = 2
  const dataSize = frameCount * channelCount * bytesPerSample
  const buffer = new ArrayBuffer(44 + dataSize)
  const view = new DataView(buffer)

  const writeAscii = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) view.setUint8(offset + i, value.charCodeAt(i))
  }

  writeAscii(0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeAscii(8, 'WAVE')
  writeAscii(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, channelCount, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * channelCount * bytesPerSample, true)
  view.setUint16(32, channelCount * bytesPerSample, true)
  view.setUint16(34, 16, true)
  writeAscii(36, 'data')
  view.setUint32(40, dataSize, true)

  let offset = 44
  for (let frame = 0; frame < frameCount; frame += 1) {
    for (let channel = 0; channel < channelCount; channel += 1) {
      const sample = Math.max(-1, Math.min(1, channels[channel][frame] ?? 0))
      view.setInt16(offset, sample < 0 ? Math.round(sample * 0x8000) : Math.round(sample * 0x7fff), true)
      offset += 2
    }
  }

  return new Blob([buffer], { type: 'audio/wav' })
}

/**
 * active Take blob들을 브라우저에서 decode하여 stereo PCM WAV로 믹스다운합니다.
 * 각 Take의 offsetMs는 재생 때와 동일하게 소스 앞부분을 skip하는 방식으로 적용합니다.
 */
export async function exportRecordingStudioMixToWav(tracks: RecordingStudioExportTrack[]): Promise<Blob> {
  if (tracks.length === 0) throw new Error('NO_EXPORT_TRACKS')

  const context = new AudioContext()
  try {
    const decoded: DecodedTrack[] = []
    for (const track of tracks) {
      const arrayBuffer = await track.blob.arrayBuffer()
      const audio = await context.decodeAudioData(arrayBuffer.slice(0))
      decoded.push({
        buffer: audio,
        volume: sanitizeExportVolume(track.volume),
        offsetMs: Math.max(0, track.offsetMs),
        timelineStartMs: Math.max(0, track.timelineStartMs ?? 0),
        clipStartMs: Math.max(0, track.clipStartMs ?? track.timelineStartMs ?? 0),
        clipEndMs: Number.isFinite(track.clipEndMs) ? Math.max(0, Number(track.clipEndMs)) : Number.POSITIVE_INFINITY,
      })
    }

    const sampleRate = Math.max(...decoded.map((track) => track.buffer.sampleRate))
    const maxFrames = Math.max(
      1,
      ...decoded.map((track) => {
        const audibleDurationMs = Math.max(0, track.buffer.duration * 1000 - track.offsetMs)
        const naturalEndMs = track.timelineStartMs + audibleDurationMs
        const effectiveStartMs = Math.max(track.timelineStartMs, track.clipStartMs)
        const effectiveEndMs = Math.min(naturalEndMs, track.clipEndMs)
        return effectiveEndMs > effectiveStartMs
          ? Math.max(1, Math.ceil((effectiveEndMs / 1000) * sampleRate))
          : 1
      }),
    )
    const output = [new Float32Array(maxFrames), new Float32Array(maxFrames)]

    for (const track of decoded) {
      const sourceRate = track.buffer.sampleRate
      const skipSeconds = track.offsetMs / 1000
      const audibleDurationMs = Math.max(0, track.buffer.duration * 1000 - track.offsetMs)
      const naturalEndMs = track.timelineStartMs + audibleDurationMs
      const effectiveStartMs = Math.max(track.timelineStartMs, track.clipStartMs)
      const effectiveEndMs = Math.min(naturalEndMs, track.clipEndMs)
      if (effectiveEndMs <= effectiveStartMs) continue

      const startFrame = Math.max(0, Math.round((effectiveStartMs / 1000) * sampleRate))
      const endFrame = Math.min(maxFrames, Math.ceil((effectiveEndMs / 1000) * sampleRate))
      for (let outFrame = startFrame; outFrame < endFrame; outFrame += 1) {
        const projectTimeSeconds = outFrame / sampleRate
        const sourceTime = skipSeconds + projectTimeSeconds - track.timelineStartMs / 1000
        const sourceFrame = Math.floor(sourceTime * sourceRate)
        if (sourceFrame < 0 || sourceFrame >= track.buffer.length) continue

        const left = track.buffer.getChannelData(0)[sourceFrame] ?? 0
        const right = track.buffer.numberOfChannels > 1
          ? track.buffer.getChannelData(1)[sourceFrame] ?? left
          : left

        output[0][outFrame] += left * track.volume
        output[1][outFrame] += right * track.volume
      }
    }

    // Hard clipping을 피하기 위해 peak가 1을 넘는 경우에만 전체 gain을 내려줍니다.
    let peak = 0
    for (const channel of output) {
      for (const sample of channel) peak = Math.max(peak, Math.abs(sample))
    }
    if (peak > 1) {
      const gain = 1 / peak
      for (const channel of output) {
        for (let i = 0; i < channel.length; i += 1) channel[i] *= gain
      }
    }

    return encodeWav(output, sampleRate)
  } finally {
    await context.close()
  }
}
